import { addPathPrefix, getWithoutNilQuery } from '@/utils/path';
import { useRouter } from '@/router/useRouter';
import { useStore } from '@/store';
import { fetchProjectInfo } from '@/store/project/project';
import { aiEditorRemoteRouteName, aiEditorAutoRouteName } from '@/router/route-const';
function resolveProjectId(query) {
    const { route } = useRouter();
    return query.projectid || route.value?.query?.projectid;
}
/**
 * 尝试从 store 当前项目同步读出目标项目的 useRemoteEditor。
 * 无 projectId、或目标即 store 当前项目时可同步决策；否则返回 undefined 交由异步拉取。
 */
function readRemoteEditorFromStore(projectId) {
    const current = useStore().state.project.projectInfo;
    if (!projectId || current?.id === projectId) {
        return current?.aiAdvanceConfig?.useRemoteEditor === true;
    }
    return undefined;
}
async function fetchRemoteEditorFlag(projectId) {
    try {
        const info = await fetchProjectInfo(projectId);
        return info?.aiAdvanceConfig?.useRemoteEditor === true;
    }
    catch {
        // 拉取失败兜底走本地（原版）编辑器
        return false;
    }
}
/** 同步优先（store）+ 异步拉取，供路由守卫等决策是否进远程编辑器 */
export async function resolveUseRemoteEditor(projectId) {
    const cached = readRemoteEditorFromStore(projectId);
    if (cached !== undefined) {
        return cached;
    }
    if (!projectId) {
        return false;
    }
    return fetchRemoteEditorFlag(projectId);
}
/**
 * 进入 AI 编辑器：根据「目标项目」的 aiAdvanceConfig.useRemoteEditor 决定进入 remote 版还是本地（原版）编辑器。
 * - 目标项目即 store 当前项目（常见情况）时同步决策，不额外发请求，也不破坏 newWindow 弹窗的用户手势；
 * - 目标项目不在 store（如创建流程跨项目跳转）时，按 projectid 异步拉取配置后再跳转。
 */
export function openAiEditor(query = {}, newWindow = false) {
    const projectId = resolveProjectId(query);
    const cached = readRemoteEditorFromStore(projectId);
    if (cached !== undefined) {
        (cached ? openAiEditorRemote : openAiProjectEditor)(query, newWindow);
        return;
    }
    void (async () => {
        const useRemote = await resolveUseRemoteEditor(projectId);
        (useRemote ? openAiEditorRemote : openAiProjectEditor)(query, newWindow);
    })();
}
export function getGogoEditorHref(projectId) {
    return addPathPrefix(`/gogo/editor?projectid=${projectId}`);
}
export function openGogoEditor(projectId) {
    window.location.href = getGogoEditorHref(projectId);
}
export function getAiProjectEditorHref(query = {}) {
    const { router, route } = useRouter();
    const projectId = query.projectid || route.value?.query?.projectid;
    const routeQuery = getWithoutNilQuery({
        ...query,
        projectid: projectId,
        branch: query.branch || route.value?.query?.branch,
    });
    return router.value?.resolve({ name: aiEditorAutoRouteName, query: routeQuery })?.href;
}
export function openAiProjectEditor(query = {}, newWindow = false) {
    const url = getAiProjectEditorHref(query);
    if (newWindow) {
        window.open(url, '_blank', 'noopener=yes');
    }
    else {
        window.location.href = url;
    }
}
export function getAiEditorRemoteHref(query = {}) {
    const { router, route } = useRouter();
    const projectId = query.projectid || route.value?.query?.projectid;
    const routeQuery = getWithoutNilQuery({
        ...query,
        projectid: projectId,
        branch: query.branch || route.value?.query?.branch,
    });
    return router.value?.resolve({ name: aiEditorRemoteRouteName, query: routeQuery })?.href;
}
export function openAiEditorRemote(query = {}, newWindow = false) {
    const url = getAiEditorRemoteHref(query);
    if (newWindow) {
        window.open(url, '_blank', 'noopener=yes');
    }
    else {
        window.location.href = url;
    }
}
/**
 * 打开AI编辑器并删除页面文件
 */
export function openAiProjectEditorToDeletePage(options, newWindow = false) {
    const { projectid, pageId } = options;
    const fileName = pageId === 'aipage' ? 'index' : pageId.replace(/^aipage-/, '');
    openAiEditor({
        projectid,
        prompt: `删除文件 /pages/${fileName}.vue`,
    }, newWindow);
}
